// Mis comisiones: el monto del periodo, la próxima meta, el estado de cada regla y las barras.
// Todo sale de `GET /commissions/me`; nada se inventa (sin escalones ni bonos que ninguna regla tenga).
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionMetricEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionMetricEnum';
import { CommissionOperatorEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionOperatorEnum';
import {
    describeCondition,
    describeLevers,
    describeScope,
    percent,
    ruleKind,
    sellerPaidByRule,
    type WeekBar,
} from '@/domains/retail/admin/comisiones';
import { formatMoneyCents } from '@/shared/lib/format/money';

type Mine = RetailCommission.GetMyCommissionsResponse;
type Week = RetailCommission.CommissionSellerBreakdown;
type RuleView = RetailCommission.CommissionRuleView;

const { POSICION_VENTA_EN_PERIODO, COLOCACION_PERIODO, SKUS_DISTINTOS_PERIODO, NPI_M30_VENDEDOR } =
    CommissionMetricEnum;
const { GTE, GT, BETWEEN } = CommissionOperatorEnum;

export type HeroPeriod = 'semana' | 'mes' | 'anio';

const HERO_LABEL: Record<HeroPeriod, string> = {
    semana: 'en el corte en curso',
    mes: 'este mes',
    anio: 'este año',
};

export function heroOf(
    data: Mine,
    period: HeroPeriod,
): { label: string; amountCents: number; salesCount: number } {
    if (period === 'semana') {
        return {
            label: HERO_LABEL.semana,
            amountCents: data.week?.netCents ?? 0,
            salesCount: data.week?.salesCount ?? 0,
        };
    }
    const totals = period === 'mes' ? data.month : data.year;
    return {
        label: HERO_LABEL[period],
        amountCents: totals.netCents,
        salesCount: totals.salesCount,
    };
}

export const countsSales = (rule: RuleView) => rule.countBy === CommissionCountByEnum.SALE;

/** El valor de la métrica del vendedor en el corte, medido como el motor; `null` si no hay dato. */
export function metricNow(
    rule: RuleView,
    metric: CommissionMetricEnum,
    week: Week | null,
): number | null {
    if (metric === POSICION_VENTA_EN_PERIODO)
        return (countsSales(rule) ? week?.salesCount : week?.unitsCount) ?? 0;
    if (metric === COLOCACION_PERIODO) return week?.placementCents ?? 0;
    if (metric === SKUS_DISTINTOS_PERIODO) return week?.bySku.length ?? 0;
    return week?.npiM30Bps ?? null;
}

/** La meta de una condición "hacia arriba" (≥, > o el inicio de un rango); `null` en las demás. */
function targetOf(condition: RuleView['condition']): number | null {
    if (!condition) return null;
    const value = Array.isArray(condition.value) ? condition.value.at(0) : condition.value;
    if (value === undefined) return null;
    if (condition.operator === GTE || condition.operator === BETWEEN) return value;
    if (condition.operator === GT) return value + 1;
    return null;
}

/** Cómo se lee una cifra de una métrica. */
export function metricText(metric: CommissionMetricEnum, value: number): string {
    if (metric === COLOCACION_PERIODO) return formatMoneyCents(value);
    if (metric === NPI_M30_VENDEDOR) return percent(value);
    return String(value);
}

export interface NextGoal {
    ruleName: string;
    missing: number;
    unit: 'piezas' | 'ventas' | 'modelos';
    rewardCents: number;
}

/** El umbral por piezas, ventas o modelos distintos más cercano que todavía no paga. Un rango no es meta: pasarse no cobra. */
export function nextGoalOf(rules: readonly RuleView[], week: Week | null): NextGoal | null {
    const fired = new Set((week?.thresholds ?? []).map((t) => t.ruleId));
    let best: NextGoal | null = null;
    for (const rule of rules) {
        const threshold = rule.levers.bonoUmbral;
        const metric = rule.condition?.metric;
        if (!threshold || fired.has(rule.ruleId) || !metric) continue;
        if (metric !== POSICION_VENTA_EN_PERIODO && metric !== SKUS_DISTINTOS_PERIODO) continue;
        if (rule.condition?.operator === BETWEEN) continue;
        const target = targetOf(rule.condition);
        const current = metricNow(rule, metric, week);
        if (target === null || current === null || target <= current) continue;
        const missing = target - current;
        if (best === null || missing < best.missing) {
            best = {
                ruleName: rule.name,
                missing,
                unit:
                    metric !== POSICION_VENTA_EN_PERIODO
                        ? 'modelos'
                        : countsSales(rule)
                          ? 'ventas'
                          : 'piezas',
                rewardCents: threshold.amountCents,
            };
        }
    }
    return best;
}

export interface LeverRow {
    ruleId: string;
    name: string;
    kind: string;
    lever: string;
    condition: string;
    scope: string;
    /** Ya pagó (o su multiplicador aplica) en el corte en curso. */
    met: boolean;
    /** Lo que pagó en el corte; `null` si no pagó o si es un multiplicador (su parte va en cada regla). */
    amountCents: number | null;
    /** Avance hacia una meta "hacia arriba"; `null` si no hay una que se pueda medir. */
    progress: { metric: CommissionMetricEnum; current: number; target: number } | null;
    /** Por qué la condición del NPI todavía no se puede medir. */
    note: string | null;
}

/** Umbral y multiplicador miden todo lo vendido en el corte; la posición de una regla por venta solo su alcance. */
export const isCutRule = (rule: RuleView) =>
    rule.levers.bonoUmbral !== null || rule.levers.multiplicador !== null;

function progressOf(rule: RuleView, week: Week | null): LeverRow['progress'] {
    const condition = rule.condition;
    if (!condition) return null;
    const { metric } = condition;
    if (metric === POSICION_VENTA_EN_PERIODO && !isCutRule(rule)) return null;
    const target = targetOf(condition);
    const current = metricNow(rule, metric, week);
    if (target === null || current === null || current >= target) return null;
    return { metric, current, target };
}

/** Sin la muestra mínima o sin dato, el motor no cuenta el NPI. */
function npiNote(condition: RuleView['condition'], week: Week | null): string | null {
    if (condition?.metric !== NPI_M30_VENDEDOR) return null;
    const { minSample } = condition;
    if (minSample !== null && (week?.npiSample ?? 0) < minSample) {
        return `Necesitas al menos ${String(minSample)} ${minSample === 1 ? 'crédito' : 'créditos'} para que cuente tu NPI`;
    }
    if ((week?.npiM30Bps ?? null) === null) return 'Sin dato de NPI';
    return null;
}

/** Las reglas vigentes: primero las que faltan, después las que ya pagaron. */
export function leverRowsOf(rules: readonly RuleView[], week: Week | null): LeverRow[] {
    const paid = new Map((week ? sellerPaidByRule(week) : []).map((r) => [r.ruleId, r.paidCents]));
    const multiplying = new Set((week?.multipliers ?? []).map((m) => m.ruleId));
    return rules
        .map((rule): LeverRow => {
            const paidCents = paid.get(rule.ruleId);
            const met = paidCents !== undefined || multiplying.has(rule.ruleId);
            const note = met ? null : npiNote(rule.condition, week);
            return {
                ruleId: rule.ruleId,
                name: rule.name,
                kind: ruleKind(rule.levers),
                lever: describeLevers(
                    rule.levers,
                    rule.countBy,
                    rule.payTiming,
                    rule.onSaleAdvanceBps,
                ),
                condition: describeCondition(rule.condition, rule.countBy),
                scope: describeScope(rule.scope),
                met,
                amountCents: paidCents !== undefined && paidCents > 0 ? paidCents : null,
                progress: met || note !== null ? null : progressOf(rule, week),
                note,
            };
        })
        .sort((a, b) => Number(a.met) - Number(b.met) || a.name.localeCompare(b.name));
}

/**
 * "Tú vs. tú": los cortes anteriores que manda el motor (el de la semana pasada puede seguir abierto,
 * calculado al momento; los vacíos van en cero) y el corte en curso. El promedio y el mejor salen solo
 * de los anteriores: el en curso va a medias.
 */
export function barsOf(data: Mine): {
    bars: WeekBar[];
    averageCents: number | null;
    bestCents: number | null;
} {
    const bars: WeekBar[] = [
        ...data.history.map((point) => ({
            periodId: point.periodId,
            cents: point.paidCents,
            current: false,
            clawback: false,
        })),
        {
            periodId: data.period.periodId,
            cents: data.week?.netCents ?? 0,
            current: true,
            clawback: (data.week?.clawbackCents ?? 0) > 0,
        },
    ];
    const previous = data.history.map((point) => point.paidCents);
    if (previous.length === 0) return { bars, averageCents: null, bestCents: null };
    const total = previous.reduce((sum, cents) => sum + cents, 0);
    return {
        bars,
        averageCents: Math.round(total / previous.length),
        bestCents: Math.max(...previous),
    };
}
